import axios from 'axios';
import * as types from '../types';
import {API_URL, MFA_API_URL, MERCHANT_API_URL} from '../../common/configs/index';
import {sendToParent, setPopupWindow, TYPES} from '../../common/events';
import {open} from '../utils/popupWindow';

const BACKEND_URL = API_URL;

function getURls(is_oauth2) {
  if (is_oauth2) {
    return {
      loginURL: `${BACKEND_URL}oauth2/login`,
      registrationURL: `${BACKEND_URL}oauth2/user`,
      resendConfirm: `${BACKEND_URL}oauth2/user/resend_confirmation_link`,
      loginProxyURL: `${BACKEND_URL}proxy/login`,
      registrationProxyURL: `${BACKEND_URL}proxy/registration`,
      tokenUpdateURL: `${BACKEND_URL}oauth2/email/confirmation/request`
    };
  }
  return {
    loginURL: `${BACKEND_URL}login`,
    registrationURL: `${BACKEND_URL}user`,
    resendConfirm: `${BACKEND_URL}user/resend_confirmation_link`,
    loginProxyURL: `${BACKEND_URL}proxy/login`,
    registrationProxyURL: `${BACKEND_URL}proxy/registration`,
    tokenUpdateURL: `${BACKEND_URL}token/update`
  };
}

export function setFields(fields) {
  return {
    type: types.SET_FIELDS,
    fields
  };
}

export function setSocialProviderInfo(social_provider, email_from_provider, publisher_account) {
  return {
    type: types.SOCIAL_PROVIDER_INFO,
    social_provider,
    email_from_provider,
    publisher_account
  };
}

/**
 * Ask for using email for promo actions
 */
export function askForEmailPromo() {
  return {
    type: types.SET_EMAIL_PROMO
  };
}

/**
 * Login async request
 *
 * @param dispatch
 * @param user
 * @param locationQuery
 * @param store
 */
export function loginAsync(dispatch, user, locationQuery, store, success) {
  dispatch({type: types.LOGIN_ATTEMPT, user});

  const loginURLs = getURls(store.is_oauth2);

  let url;
  if (store.proxy.login) {
    url = loginURLs.loginProxyURL;
  } else {
    url = loginURLs.loginURL;
  }

  let headers = {};
  if (store.captcha.value) {
    headers = {'X-Captcha-Token': store.captcha.value};
  } else if (store.chineseCaptcha.values) {
    headers = {
      'X-Captcha-Challenge': store.chineseCaptcha.values.challenge,
      'X-Captcha-Validate': store.chineseCaptcha.values.validate,
      'X-Captcha-Seccode': store.chineseCaptcha.values.seccode,
      'X-Captcha-Success': success
    };
  }

  let params = {};
  if (store.is_oauth2 && !store.proxy.login) {
    params = {
      client_id: locationQuery.client_id,
      scope: locationQuery.scope,
      state: locationQuery.state,
      redirect_uri: locationQuery.redirect_uri,
      response_type: locationQuery.response_type
    };
  } else {
    params = {
      projectId: locationQuery.projectId,
      redirect_url: locationQuery.redirectUrl,
      with_logout: locationQuery.withLogout,
      payload: locationQuery.payload,
      login_url: locationQuery.login_url
    };
  }

  return axios({
    method: 'POST',
    url,
    withCredentials: true,
    params,
    data: {
      username: user.email || user.username,
      password: user.password,
      remember_me: user.remember_me
    },
    headers
  })
    .then(response => {
      dispatch({
        type: types.LOGIN_SUCCESSFULLY,
        meta: {
          analytics: {
            type: 'Form auth',
            payload: null
          }
        }
      });

      if (typeof window !== 'undefined') {
        const callbackUrl = response.data.login_url;
        if (store.externalWindow) {
          setPopupWindow(open(callbackUrl, 'auth', 1000, 800));
        } else {
          window.top.location.href = callbackUrl;
        }
      }

      return response;
    })
    .catch(error => {
      if (
        error.response &&
        error.response.data.error &&
        error.response.data.error.code === '010-007'
      ) {
        dispatch({type: types.SHOW_CAPTCHA});
      } else if (
        error.response &&
        error.response.data.error &&
        error.response.data.error.code === '010-016'
      ) {
        dispatch({type: types.SHOW_CHINESE_CAPTCHA});
      } else if (
        error.response &&
        error.response.data.error &&
        error.response.data.error.code === '003-007'
      ) {
        dispatch({type: types.RESEND_CONFIRM_INIT, email: user.email || user.username});
        dispatch({type: types.LOGIN_STOP});
      } else {
        dispatch({
          type: types.LOGIN_FAILED,
          error: error.response ? error.response.data.error.code : error.message
        });
      }

      if (store.captcha.value) {
        dispatch({type: types.REMOVE_RECAPTCHA});
      }

      if (store.chineseCaptcha.values) {
        dispatch({type: types.REMOVE_CHINESE_CAPTCHA});
      }

      return error;
    });
}

/**
 * Reset password async request
 * @param dispatch
 * @param user
 * @param locationQuery
 * @param store
 */
export function resetAsync(dispatch, user, locationQuery, store) {
  dispatch({type: types.RESET_ATTEMPT, user});

  const url =
    BACKEND_URL + (store.proxy.resetPassword ? 'proxy/password/reset' : 'password/reset/request');

  const value = user.email || user.username;

  return axios({
    method: 'POST',
    url,
    withCredentials: true,
    params: {
      projectId: locationQuery.projectId,
      login_url: locationQuery.login_url
    },
    data: {
      email: value,
      username: value
    }
  })
    .then(response => {
      dispatch({
        type: types.RESET_SUCCESSFULLY,
        meta: {
          analytics: {
            type: 'Form reset password',
            payload: null
          }
        }
      });

      return response;
    })
    .catch(error => {
      dispatch({
        type: types.RESET_FAILED,
        error: error.response ? error.response.data.error.code : error.message
      });
      return error;
    });
}

/**
 * Set password async request
 * @param dispatch
 * @param user
 * @param locationQuery
 */
export function setAsync(dispatch, user, locationQuery) {
  dispatch({type: types.SET_ATTEMPT, user});

  const url = `${BACKEND_URL}password/reset/confirm`;

  return axios({
    method: 'POST',
    url,
    withCredentials: true,
    params: {
      projectId: locationQuery.projectId,
      login_url: locationQuery.login_url
    },
    data: {
      user_id: user.user_id,
      reset_code: user.code,
      new_password: user.password
    }
  })
    .then(response => {
      dispatch({type: types.SET_SUCCESSFULLY});

      return response;
    })
    .catch(error => {
      dispatch({
        type: types.SET_FAILED,
        error: error.response
          ? error.response.data.error.code || error.response.data.error.description
          : error.message
      });
      return error;
    });
}

/**
 * Resend confirm letter
 * @param dispatch
 * @param email
 * @para locationQuery
 * @param store
 */

export function resendConfirmAsync(dispatch, value, locationQuery, store) {
  dispatch({type: types.RESEND_CONFIRM_ATTEMPT});

  const loginURLs = getURls(store.is_oauth2);
  const url = loginURLs.resendConfirm;

  const data = {
    username: value
  };

  let params = {};
  if (store.is_oauth2 && !store.proxy.registration) {
    params = {
      client_id: locationQuery.client_id,
      payload: locationQuery.payload
    };
  } else {
    params = {
      projectId: locationQuery.projectId,
      redirect_url: locationQuery.redirectUrl,
      payload: locationQuery.payload,
      login_url: locationQuery.login_url
    };
  }

  return axios({
    method: 'POST',
    url,
    withCredentials: true,
    params,
    data
  })
    .then(response => {
      dispatch({
        type: types.RESEND_CONFIRM_SUCCESS
      });

      return response;
    })
    .catch(error => {
      dispatch({
        type: types.RESEND_CONFIRM_ERROR,
        error: error.response ? error.response.data.error.code : error.message
      });

      return error;
    });
}

/**
 * Registration async request
 * @param dispatch
 * @param user
 * @param locationQuery
 * @param store
 */
export function registrationAsync(dispatch, user, locationQuery, store, success) {
  let headers = {};
  dispatch({type: types.REGISTRATION_ATTEMPT, user});

  if (store.captcha.value) {
    headers = {'X-Captcha-Token': store.captcha.value};
  } else if (store.chineseCaptcha.values) {
    headers = {
      'X-Captcha-Challenge': store.chineseCaptcha.values.challenge,
      'X-Captcha-Validate': store.chineseCaptcha.values.validate,
      'X-Captcha-Seccode': store.chineseCaptcha.values.seccode,
      'X-Captcha-Success': success
    };
  }
  const loginURLs = getURls(store.is_oauth2);
  let url;
  if (store.proxy.registration) {
    url = loginURLs.registrationProxyURL;
  } else {
    url = loginURLs.registrationURL;
  }
  const {email, password, consent, promo_email_agreement, ...secondary} = user;
  const data = {
    email,
    password,
    username: email,
    accept_consent: consent
  };

  let params = {};
  if (store.is_oauth2 && !store.proxy.registration) {
    params = {
      client_id: locationQuery.client_id,
      scope: locationQuery.scope,
      state: locationQuery.state,
      redirect_uri: locationQuery.redirect_uri,
      response_type: locationQuery.response_type
    };
  } else {
    params = {
      projectId: locationQuery.projectId,
      redirect_url: locationQuery.redirectUrl,
      payload: locationQuery.payload,
      login_url: locationQuery.login_url
    };
  }

  if (Object.keys(secondary).length) {
    if (secondary.username) {
      const {username, ...secondaryWithoutUsername} = secondary;
      data.username = username;
      data.fields = secondaryWithoutUsername;
    } else {
      data.fields = secondary;
    }
  }

  if (Object.prototype.hasOwnProperty.call(user, 'promo_email_agreement')) {
    data.promo_email_agreement = promo_email_agreement ? 1 : 0;
  }

  const PUBLISHER_ACCOUNT_PROJECT_ID = '40db2ea4-5d42-11e6-a3ff-005056a0e04a';
  let redirectUrl = '';

  return axios({
    method: 'POST',
    url,
    withCredentials: true,
    params,
    data,
    headers
  })
    .then(response => {
      if (response.data.login_url) {
        redirectUrl = response.data.login_url;
      }

      return response;
    })
    .then(response => {
      const {projectId} = locationQuery;

      if (projectId === PUBLISHER_ACCOUNT_PROJECT_ID) {
        return axios({
          method: 'POST',
          url: `${MERCHANT_API_URL}merchant/xsolla_login/registration`,
          data: {
            email: user.email,
            username: user.email,
            password: user.password,
            payload: locationQuery.payload
          }
        });
      }

      return response;
    })
    .then(() => {
      dispatch({
        type: types.REGISTRATION_SUCCESSFULLY,
        meta: {
          analytics: {
            type: 'Form registration',
            payload: null
          }
        },
        resend: {
          email: user.username && user.email !== user.username ? user.username : user.email
        }
      });

      sendToParent(TYPES.REGISTRATION_REQUEST);

      if (typeof window !== 'undefined' && redirectUrl) {
        window.top.location.href = redirectUrl;
      }
    })
    .catch(error => {
      const {projectId} = locationQuery;

      if (
        error.response &&
        error.response.data.error &&
        error.response.data.error.code === '010-007'
      ) {
        dispatch({type: types.SHOW_CAPTCHA});
      } else if (
        error.response &&
        error.response.data.error &&
        error.response.data.error.code === '010-016'
      ) {
        dispatch({type: types.SHOW_CHINESE_CAPTCHA});
      } else if (error.response && error.response.data.error && error.response.data.error.code) {
        dispatch({
          type: types.REGISTRATION_FAILED,
          error: error.response.data.error.code
        });
      } else if (
        projectId === PUBLISHER_ACCOUNT_PROJECT_ID &&
        error.response &&
        error.response.data.http_status_code === 404
      ) {
        // Temporary error handling from Merchant API
        dispatch({
          type: types.REGISTRATION_FAILED,
          error: '003-004'
        });
      } else {
        dispatch({
          type: types.REGISTRATION_FAILED,
          error: error.message
        });
      }

      if (store.captcha.value) {
        dispatch({type: types.REMOVE_RECAPTCHA});
      }

      if (store.chineseCaptcha.values) {
        dispatch({type: types.REMOVE_CHINESE_CAPTCHA});
      }

      return error;
    });
}

export function resetRegistration(dispatch) {
  dispatch({type: types.REGISTRATION_RESET});
}

/**
 * Ask async request
 * @param dispatch
 * @param fields
 * @param locationQuery
 */
export function askAsync(dispatch, fields, locationQuery, store) {
  dispatch({type: types.ASK_ATTEMPT, fields});

  if (Object.prototype.hasOwnProperty.call(fields, 'promo_email_agreement')) {
    // eslint-disable-next-line no-param-reassign
    fields.promo_email_agreement = fields.promo_email_agreement ? 1 : 0;
  }

  const loginURLs = getURls(store.is_oauth2 || locationQuery.is_oauth2);
  const url = loginURLs.tokenUpdateURL;
  let params = {};
  let data = {};
  if (store.is_oauth2 || locationQuery.is_oauth2) {
    params = {
      client_id: locationQuery.client_id
    };
    data = {
      token: locationQuery.token,
      updatedFields: fields,
      email: fields.email
    };
  } else {
    params = {
      projectId: locationQuery.projectId,
      login_url: locationQuery.login_url
    };
    data = {
      token: locationQuery.token,
      updatedFields: fields,
      tmp_session: locationQuery.tmp_session
    };
  }
  axios({
    method: 'POST',
    url,
    withCredentials: true,
    params,
    data
  })
    .then(response => {
      if (response.data.error && response.data.error.code === '003-011') {
        dispatch({type: types.ASK_CONFIRM, response});
      }

      dispatch({type: types.ASK_SUCCESSFULLY, response});

      if (typeof window !== 'undefined' && response.data.redirect_url) {
        window.top.location.href = response.data.redirect_url;
      }
    })
    .catch(error => {
      dispatch({
        type: types.ASK_FAILED,
        error: error.response ? error.response.data.error.code : error.message
      });
    });
}

/**
 * Register chinese captcha async request
 * @param dispatch
 */
export function registerChineseCaptchaAsync(dispatch, initGeetest, callback) {
  axios({
    method: 'GET',
    url: `${BACKEND_URL}captcha`
  })
    .then(response => {
      let data;
      if (typeof response.data === 'string') {
        data = JSON.parse(response.data);
      } else {
        data = response.data; // eslint-disable-line prefer-destructuring
      }

      dispatch({type: types.CAPTCHA_REGISTER_SUCCESSFULLY, success: data.success});

      initGeetest(
        {
          gt: data.gt,
          challenge: data.challenge,
          new_captcha: data.new_captcha,
          product: 'embed',
          offline: !data.success
        },
        callback
      );
    })
    .catch(error => {
      dispatch({type: types.HIDE_CHINESE_CAPTCHA});

      dispatch({
        type: types.CAPTCHA_REGISTER_FAILED,
        error: error.response ? error.response.data.error.code : error.message
      });
    });
}

export function otpLogin(dispatch, {code}, locationQuery) {
  dispatch({type: types.OTP_LOGIN_ATTEMPT});

  axios({
    method: 'GET',
    url: `${MFA_API_URL}otp/validate`,
    withCredentials: true,
    params: {
      code,
      challenge_id: locationQuery.challenge_id,
      remember_me: true
    }
  })
    .then(response => {
      dispatch({type: types.OTP_LOGIN_SUCCESSFULLY});

      if (typeof window !== 'undefined' && response.data.url) {
        window.top.location.href = response.data.url;
      }
    })
    .catch(error => {
      dispatch({
        type: types.OTP_LOGIN_FAILED,
        error: error.response ? error.response.data.code : error.message
      });
    });
}

export function otpResendPassword(dispatch, {locale, challenge_id}) {
  dispatch({type: types.OTP_RESEND_ATTEMPT});

  axios({
    method: 'PUT',
    url: `${MFA_API_URL}otp/resend`,
    params: {
      challenge_id
    },
    data: {
      locale
    }
  }).catch(error => {
    dispatch({
      type: types.OTP_RESEND_FAILED,
      error: error.response ? error.response.data.code : error.message
    });
  });
}

export function otpResendTimerTick(dispatch) {
  dispatch({type: types.OTP_RESEND_TIMER_TICK});
}

/**
 * Set email address
 * @param dispatch
 * @param email
 */
export function setEmail(dispatch, email) {
  dispatch({type: types.SET_EMAIL, email});
}

/**
 * Set username field
 * @param dispatch
 * @param username
 */
export function setUsername(dispatch, username) {
  dispatch({type: types.SET_USERNAME, username});
}

/**
 * Clear errors
 * @param dispatch
 */
export function clearErrors(dispatch) {
  dispatch({type: types.CLEAR_ERRORS});
}



// WEBPACK FOOTER //
// ./actions/auth.js